<template>
  <div class="cashback-page">
    <!-- Cashback Card -->
    <div class="cashback-card">
      <!-- Header -->
      <div class="card-header">
        <span class="header-icon">🎁</span>
        <span class="header-title">คืนยอดเสีย</span>
      </div>

      <!-- Card Body -->
      <div class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="state-box loading">
          <div class="loading-spinner"></div>
          <span>กำลังโหลด...</span>
        </div>

        <!-- Load Error State -->
        <div v-else-if="loadError" class="state-box error">
          <span class="state-icon">⚠️</span>
          <span class="state-text">เกิดข้อผิดพลาดในการโหลดข้อมูล</span>
          <button class="retry-btn" @click="getCashback">
            ลองใหม่อีกครั้ง
          </button>
        </div>

        <!-- Empty State -->
        <div v-else-if="empty" class="state-box empty">
          <span class="state-icon">📅</span>
          <span class="state-text">ไม่มีคืนยอดเสียในขณะนี้</span>
          <span class="state-hint"
            >ยอดเสียจากการเล่นวันนี้ รับคืนได้พรุ่งนี้หลัง 00:15น</span
          >
        </div>

        <!-- Already Claimed State -->
        <div v-else-if="alreadyClaimed" class="state-box claimed">
          <span class="state-icon">✅</span>
          <span class="state-text">รับเครดิตคืนยอดเสียวันนี้แล้ว</span>
          <span v-if="cashback > 0" class="state-amount"
            >ได้รับ ฿{{ formatNumber(cashback) }}</span
          >
          <span class="state-hint">กลับมาใหม่พรุ่งนี้หลัง 00:15น</span>
        </div>

        <!-- Expired State -->
        <div v-else-if="isExpired" class="state-box empty">
          <span class="state-icon">⏰</span>
          <span class="state-text">คืนยอดเสียหมดอายุแล้ว</span>
          <span class="state-hint"
            >รับได้ภายใน {{ settings.claim_expiry_hours }} ชม.
            หลังระบบคำนวณ</span
          >
        </div>

        <!-- Cashback Available -->
        <div v-else class="claim-section">
          <!-- Amount Display -->
          <div class="amount-box">
            <span class="amount-label">ยอดที่รับได้</span>
            <div class="amount-row">
              <span class="amount-value">฿{{ formatNumber(cashback) }}</span>
              <span v-if="shouldShowWarning" class="turnover-tag"
                >ทำเทิร์น 1x</span
              >
            </div>
            <span v-if="formulaText" class="formula-hint">{{
              formulaText
            }}</span>
          </div>

          <!-- Expiry Countdown -->
          <div
            v-if="countdownText"
            class="countdown-chip"
            :class="{ urgent: isNearExpiry }"
          >
            <span>⏳</span>
            <span
              >เหลือเวลารับอีก {{ countdownText }} (ถึง
              {{ expiryTimeText }})</span
            >
          </div>

          <!-- Below Minimum Notice -->
          <div v-if="belowMin" class="min-amount-remark">
            <span class="remark-icon">ℹ️</span>
            <span
              >ยอดคืน ฿{{ formatNumber(cashback) }} ยังไม่ถึงขั้นต่ำ ฿{{
                minClaim
              }}
              จึงยังกดรับไม่ได้</span
            >
          </div>

          <!-- Credit Ceiling Notice -->
          <div v-else-if="creditTooHigh" class="credit-warning">
            <span class="remark-icon">⚠️</span>
            <span
              >เครดิตคงเหลือ ฿{{ formatNumber(memberCredit) }} — ต้องไม่เกิน ฿{{
                settings.max_credit_to_claim
              }}
              จึงจะกดรับได้
              <button class="link-btn" @click="fetchMemberCredit">
                เช็คเครดิตอีกครั้ง
              </button></span
            >
          </div>

          <!-- Claim Button -->
          <button
            class="claim-btn"
            :class="{ disabled: !canClaimCashback, claiming: isClaiming }"
            :disabled="!canClaimCashback || isClaiming"
            @click="showConfirmation"
          >
            <span v-if="isClaiming" class="btn-spinner"></span>
            <span>{{ claimButtonText }}</span>
          </button>
        </div>
      </div>

      <!-- Terms Footer -->
      <div class="card-footer">
        <span
          >คืนยอดเสีย {{ ratePercentText }}% สูงสุด
          {{ formatNumber(settings.cashback_max) }}฿ • รับได้ทุกวันหลัง
          00:15น</span
        >
      </div>
    </div>

    <!-- Calculation Breakdown Card -->
    <div v-if="!isLoading && !loadError && cashbackData" class="stats-card">
      <div class="stats-header">
        <span class="stats-icon">🧮</span>
        <span class="stats-title">วิธีคำนวณคืนยอดเสีย</span>
      </div>

      <div class="stats-body">
        <!-- Turnover (informational) -->
        <div class="stat-row">
          <span class="stat-label">ยอดเทิร์น</span>
          <span class="stat-value">{{ formatNumber(stats.turnover) }}</span>
        </div>

        <!-- Actual play win/loss -->
        <div class="stat-row">
          <span class="stat-label">แพ้/ชนะจากการเล่น</span>
          <span
            class="stat-value"
            :class="playWinLoss >= 0 ? 'positive' : 'negative'"
          >
            {{ playWinLoss >= 0 ? '+' : '' }}{{ formatNumber(playWinLoss) }}
          </span>
        </div>

        <template v-if="hasBonusAdjustment">
          <!-- Bonus deducted -->
          <div class="stat-row">
            <span class="stat-label"
              >โบนัสที่ได้รับ (หักออก)
              <button class="link-btn" @click="showBonusList = !showBonusList">
                {{ showBonusList ? 'ซ่อนรายการ' : 'ดูรายการ' }}
              </button></span
            >
            <span class="stat-value positive"
              >+{{ formatNumber(stats.bonus) }}</span
            >
          </div>

          <!-- Bonus source list -->
          <div v-if="showBonusList" class="bonus-list">
            <div
              v-for="tx in bonusTransactions"
              :key="tx.id"
              class="bonus-list-row"
            >
              <span>{{ tx.time }}น. ฝาก ฿{{ formatNumber(tx.amount) }}</span>
              <span>โบนัส +฿{{ formatNumber(tx.bonus) }}</span>
            </div>
          </div>

          <!-- Net loss used for calculation -->
          <div class="stat-row net-row">
            <span class="stat-label">ยอดเสียสุทธิ (ใช้คำนวณ)</span>
            <span
              class="stat-value"
              :class="stats.winLoss >= 0 ? 'positive' : 'negative'"
            >
              {{ stats.winLoss >= 0 ? '+' : ''
              }}{{ formatNumber(stats.winLoss) }}
            </span>
          </div>
        </template>

        <!-- Rate -->
        <div class="stat-row">
          <span class="stat-label">อัตราคืนยอดเสีย</span>
          <span class="stat-value">{{ ratePercentText }}%</span>
        </div>

        <!-- Result -->
        <div class="stat-row total-row">
          <span class="stat-label">คืนยอดเสียที่ได้รับ</span>
          <span class="stat-value highlight"
            >฿{{ formatNumber(stats.cashbackAmount) }}</span
          >
        </div>
      </div>

      <!-- Why-bonus-deducted note -->
      <div v-if="hasBonusAdjustment" class="calc-note">
        * โบนัสจากเงินฝากจะถูกหักออกจากยอดเสียก่อนคำนวณ
        ระบบคืนเฉพาะยอดเสียจากเงินฝากจริงเท่านั้น
      </div>

      <!-- Remark -->
      <div class="stats-remark">
        หมายเหตุ: ข้อมูลของเมื่อวันที่ {{ yesterdayDate }} - {{ todayDate }}
      </div>
    </div>

    <!-- Bottom Sheet Modal -->
    <transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click="hideModal">
        <div class="bottom-sheet" @click.stop>
          <!-- Drag Handle -->
          <div class="sheet-handle"></div>

          <!-- Sheet Content -->
          <div class="sheet-content">
            <h3 class="sheet-title">ยืนยันการรับยอดเสีย</h3>

            <!-- Amount Display -->
            <div class="confirm-amount">
              <span class="confirm-label">จำนวนที่จะรับ</span>
              <span class="confirm-value">฿{{ formatNumber(cashback) }}</span>
            </div>

            <!-- Warning Notice -->
            <div class="confirm-notice">
              <span class="notice-icon">⚠️</span>
              <span class="notice-text">ต้องทำ 1 เทิร์นก่อนถอน</span>
            </div>

            <!-- Expiry Acknowledge -->
            <div v-if="expiryTimeText" class="confirm-notice">
              <span class="notice-icon">⏰</span>
              <span class="notice-text"
                >ต้องกดรับภายใน
                {{ expiryTimeText }}
                หากเกินเวลาจะไม่สามารถรับได้</span
              >
            </div>

            <!-- Action Buttons -->
            <div class="sheet-actions">
              <button
                class="btn-cancel"
                @click="hideModal"
                :disabled="isClaiming"
              >
                ยกเลิก
              </button>
              <button
                class="btn-confirm"
                :disabled="isClaiming"
                @click="claimCashback"
              >
                <span v-if="isClaiming" class="btn-spinner"></span>
                <span>{{ isClaiming ? 'กำลังดำเนินการ...' : 'รับทราบ' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CashbackPage',
  data() {
    return {
      cashbackData: null,
      transactionData: null,
      // Mirrors the backend defaults; overwritten by getCashbackV2 response
      settings: {
        min_claim_amount: 5,
        max_credit_to_claim: 5,
        cashback_rate: 0.05,
        cashback_max: 500,
        claim_expiry_hours: 24
      },
      expiresAt: null,
      expired: false,
      empty: false,
      loadError: false,
      isLoading: false,
      isClaiming: false,
      showModal: false,
      memberCredit: null,
      showBonusList: false,
      now: Date.now(),
      countdownTimer: null
    }
  },
  computed: {
    stats() {
      if (!this.cashbackData) {
        return {
          turnover: 0,
          winLoss: 0,
          cashbackAmount: 0,
          oriWinLoss: 0,
          bonus: 0
        }
      }
      return {
        turnover: this.cashbackData.turnover || 0,
        winLoss: this.cashbackData.winlose || 0,
        cashbackAmount: this.cashbackData.cashback || 0,
        oriWinLoss: this.cashbackData.original_winlose || 0,
        bonus: this.transactionData?.totalBonus || 0
      }
    },
    cashback() {
      return this.cashbackData?.cashback || 0
    },
    minClaim() {
      return this.settings.min_claim_amount
    },
    ratePercentText() {
      return parseFloat((this.settings.cashback_rate * 100).toFixed(2))
    },
    expiryTimeText() {
      if (!this.expiresAt) return ''
      const [datePart, timePart] = String(this.expiresAt).split(' ')
      if (!datePart || !timePart) return ''
      const [year, month, day] = datePart.split('-')
      return `${day}/${month}/${year} ${timePart.slice(0, 5)}น`
    },
    expiresAtMs() {
      if (!this.expiresAt) return null
      const [datePart, timePart] = String(this.expiresAt).split(' ')
      if (!datePart || !timePart) return null
      const [year, month, day] = datePart.split('-').map(Number)
      const [hour, minute, second] = timePart.split(':').map(Number)
      return new Date(year, month - 1, day, hour, minute, second || 0).getTime()
    },
    remainingMs() {
      if (this.expiresAtMs === null) return null
      return this.expiresAtMs - this.now
    },
    countdownText() {
      if (this.remainingMs === null || this.remainingMs <= 0) return ''
      const totalMinutes = Math.floor(this.remainingMs / 60000)
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return hours > 0 ? `${hours} ชม. ${minutes} นาที` : `${minutes} นาที`
    },
    isNearExpiry() {
      return (
        this.remainingMs !== null &&
        this.remainingMs > 0 &&
        this.remainingMs < 3 * 3600000
      )
    },
    // Backend flag, plus a client-side flip when the countdown crosses zero
    // while the page stays open
    isExpired() {
      if (this.expired) return true
      return this.remainingMs !== null && this.remainingMs <= 0
    },
    belowMin() {
      return this.cashback < this.minClaim
    },
    creditTooHigh() {
      return (
        this.memberCredit !== null &&
        this.memberCredit > this.settings.max_credit_to_claim
      )
    },
    hasBonusAdjustment() {
      return parseFloat(this.cashbackData?.bonus_deducted || 0) > 0
    },
    // Actual play result before the bonus deduction
    playWinLoss() {
      if (!this.cashbackData) return 0
      return parseFloat(
        this.cashbackData.original_winlose ?? this.cashbackData.winlose ?? 0
      )
    },
    bonusTransactions() {
      return (this.transactionData?.lists || []).filter(
        tx => parseFloat(tx.bonus) > 0
      )
    },
    formulaText() {
      if (!this.cashbackData || this.cashback <= 0) return ''
      if (parseFloat(this.cashback) >= this.settings.cashback_max) {
        return `ได้รับเต็มเพดานสูงสุด ฿${this.formatNumber(
          this.settings.cashback_max
        )}`
      }
      const lossBase = Math.abs(parseFloat(this.cashbackData.winlose))
      if (this.hasBonusAdjustment) {
        return `= ยอดเสียสุทธิ ฿${this.formatNumber(lossBase)} × ${
          this.ratePercentText
        }% (หักโบนัส ฿${this.formatNumber(
          this.cashbackData.bonus_deducted
        )} แล้ว)`
      }
      return `= ยอดเสีย ฿${this.formatNumber(lossBase)} × ${
        this.ratePercentText
      }%`
    },
    claimButtonText() {
      if (this.isClaiming) return 'กำลังดำเนินการ...'
      if (this.belowMin) return `ยอดไม่ถึงขั้นต่ำ ฿${this.minClaim}`
      if (this.creditTooHigh) {
        return `เครดิตต้องไม่เกิน ฿${this.settings.max_credit_to_claim} จึงจะรับได้`
      }
      return 'รับเครดิต'
    },
    canClaimCashback() {
      if (!this.cashbackData || this.isExpired) return false
      if (this.belowMin || this.creditTooHigh) return false
      return this.cashbackData.cash_status === 0
    },
    shouldShowWarning() {
      return this.cashback >= this.minClaim
    },
    alreadyClaimed() {
      return this.cashbackData && this.cashbackData.cash_status !== 0
    },
    // Report window derived from the record's by_date (server Thai time),
    // not the browser clock — the two can disagree around midnight
    dateWindow() {
      const byDate = this.cashbackData?.by_date
      if (!byDate) return null
      const [year, month, day] = String(byDate)
        .slice(0, 10)
        .split('-')
      if (!year || !month || !day) return null
      const end = new Date(Number(year), Number(month) - 1, Number(day))
      const start = new Date(Number(year), Number(month) - 1, Number(day) - 1)
      return { start, end }
    },
    yesterdayDate() {
      if (this.dateWindow) return this.formatDate(this.dateWindow.start)
      const yesterday = new Date()
      yesterday.setDate(yesterday.getDate() - 1)
      return this.formatDate(yesterday)
    },
    todayDate() {
      if (this.dateWindow) return this.formatDate(this.dateWindow.end)
      return this.formatDate(new Date())
    }
  },
  methods: {
    formatNumber(number) {
      return new Intl.NumberFormat('th-TH').format(number || 0)
    },
    formatDate(date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year} 00:00`
    },
    showConfirmation() {
      if (!this.canClaimCashback) return
      this.showModal = true
      document.body.style.overflow = 'hidden'
    },
    hideModal() {
      if (this.isClaiming) return
      this.showModal = false
      document.body.style.overflow = ''
    },
    async claimCashback() {
      if (this.isClaiming || !this.canClaimCashback) return

      this.isClaiming = true

      // Everything inside the try so `finally` always releases the button
      try {
        // Advisory pre-check only — the server enforces the same rule
        await this.fetchMemberCredit()

        if (this.memberCredit === null) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ไม่สามารถตรวจสอบยอดเครดิตได้',
              text: 'กรุณาลองใหม่อีกครั้ง',
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
          return
        }

        if (this.creditTooHigh) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'ไม่สามารถรับเครดิตคืนยอดเสียได้',
              text: `เครดิตคงเหลือต้องไม่เกิน ${this.settings.max_credit_to_claim} บาท`,
              icon: 'AlertCircleIcon',
              variant: 'warning'
            }
          })
          this.showModal = false
          document.body.style.overflow = ''
          return
        }

        const resp = await this.$http.post('member/retieveCashbackV2', {
          id: this.cashbackData?.id,
          type: 'cashback'
        })

        if (resp.data.success) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: resp.data.amount
                ? `รับเครดิต ฿${this.formatNumber(resp.data.amount)} สำเร็จ!`
                : 'รับเครดิตคืนยอดเสียสำเร็จ!',
              icon: 'CheckIcon',
              variant: 'success'
            }
          })
          // Reset claiming state before hiding modal
          this.isClaiming = false
          this.showModal = false
          document.body.style.overflow = ''
          // Notify header to refresh balance
          this.$eventBus.$emit('refreshBalance')
          // Refresh cashback data to update UI state
          await this.getCashback()
          await this.fetchMemberCredit()
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: resp.data.message || 'ไม่สามารถรับเครดิตได้ในขณะนี้',
              icon: 'AlertCircleIcon',
              variant: 'danger'
            }
          })
        }
      } catch (error) {
        console.error('Claim cashback error:', error)
        // Surface the backend reason (already claimed / expired / credit
        // ceiling) — non-2xx responses land here, not in the else above
        this.$toast({
          component: ToastificationContent,
          props: {
            title:
              error.response?.data?.message ||
              'เกิดข้อผิดพลาด กรุณาลองอีกครั้ง',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
        // Claim state may have changed server-side (e.g. already claimed)
        this.showModal = false
        document.body.style.overflow = ''
        await this.getCashback()
      } finally {
        this.isClaiming = false
      }
    },
    async fetchMemberCredit() {
      try {
        const resp = await this.$http.get('member/credit')
        const credit = parseFloat(resp.data?.result?.credit)
        this.memberCredit = Number.isNaN(credit) ? null : credit
      } catch (error) {
        // Unknown credit never blocks the UI; the server re-checks at claim
        this.memberCredit = null
      }
    },
    async getCashback() {
      this.isLoading = true
      this.loadError = false
      try {
        const resp = await this.$http.post('member/getCashbackV2')

        if (resp.data.settings) {
          this.settings = { ...this.settings, ...resp.data.settings }
        }

        if (resp.data.success && resp.data.cashback) {
          this.cashbackData = resp.data.cashback
          this.transactionData = resp.data.transactions
          this.expiresAt = resp.data.expires_at || null
          this.expired = resp.data.expired === true
          this.empty = false
        } else {
          this.cashbackData = null
          this.transactionData = null
          this.expiresAt = null
          this.expired = false
          this.empty = true
        }
      } catch (error) {
        console.error('Get cashback error:', error)
        // A server error is not "no cashback" — show a retryable state
        this.cashbackData = null
        this.transactionData = null
        this.empty = false
        this.loadError = true
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'เกิดข้อผิดพลาดในการโหลดข้อมูล',
            icon: 'AlertCircleIcon',
            variant: 'danger'
          }
        })
      } finally {
        this.isLoading = false
      }
    }
  },
  async mounted() {
    this.countdownTimer = setInterval(() => {
      this.now = Date.now()
    }, 30000)
    await Promise.all([this.getCashback(), this.fetchMemberCredit()])
  },
  beforeDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer)
    document.body.style.overflow = ''
  }
}
</script>

<style lang="scss" scoped>
// ==========================================
// Variables
// ==========================================
$gold: var(--theme-secondary);
$gold-light: var(--theme-secondary-light);
$card-bg: rgba(255, 255, 255, 0.05);
$border-color: rgba(255, 255, 255, 0.1);
$text-primary: #ffffff;
$text-secondary: rgba(255, 255, 255, 0.7);
$text-muted: rgba(255, 255, 255, 0.5);
$warning-bg: rgba(255, 193, 7, 0.15);
$warning-border: rgba(255, 193, 7, 0.3);

// ==========================================
// Page Container
// ==========================================
.cashback-page {
  width: 100%;
  min-height: 100%;
  padding: 16px;
  max-width: 440px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// ==========================================
// Cashback Card
// ==========================================
.cashback-card {
  background: $card-bg;
  border-radius: 14px;
  border: 1px solid $border-color;
  backdrop-filter: blur(10px);
  overflow: hidden;
}

// Card Header
.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba($gold, 0.12), rgba($gold, 0.04));
  border-bottom: 1px solid $border-color;

  .header-icon {
    font-size: 1.125rem;
  }

  .header-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #ffffff;
  }
}

// Card Body
.card-body {
  padding: 20px 16px;
}

// Card Footer
.card-footer {
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.15);
  border-top: 1px solid $border-color;
  font-size: 0.6875rem;
  color: $text-muted;
  text-align: center;
}

// ==========================================
// State Box (Loading & Empty)
// ==========================================
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px 16px;
  gap: 8px;

  &.loading {
    flex-direction: row;
    gap: 12px;
    color: $text-muted;
    font-size: 0.875rem;
  }

  &.claimed {
    .state-icon {
      font-size: 2.5rem;
      margin-bottom: 4px;
    }

    .state-text {
      color: #4ade80;
      font-weight: 500;
    }

    .state-amount {
      font-size: 1.5rem;
      font-weight: 700;
      color: $text-primary;
    }
  }

  .state-icon {
    font-size: 2.5rem;
    margin-bottom: 4px;
    opacity: 0.8;
  }

  .state-text {
    font-size: 0.9375rem;
    color: $text-secondary;
  }

  .state-hint {
    font-size: 0.75rem;
    color: $text-muted;
  }
}

.retry-btn {
  margin-top: 8px;
  padding: 8px 20px;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.08);
  color: $text-secondary;
  border: 1px solid $border-color;
  -webkit-tap-highlight-color: transparent;

  &:active {
    background: rgba(255, 255, 255, 0.12);
  }
}

.loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid rgba($gold, 0.2);
  border-radius: 50%;
  border-top-color: $gold;
  animation: spin 0.8s linear infinite;
}

// ==========================================
// Claim Section
// ==========================================
.claim-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

// Amount Box
.amount-box {
  text-align: center;
  width: 100%;

  .amount-label {
    display: block;
    font-size: 0.75rem;
    color: $text-muted;
    margin-bottom: 6px;
  }

  .amount-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .amount-value {
    font-size: 2rem;
    font-weight: 700;
    color: #ffffff;
    line-height: 1;
  }

  .turnover-tag {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #fbbf24;
    background: rgba(251, 191, 36, 0.12);
    padding: 4px 8px;
    border-radius: 6px;
    border: 1px solid rgba(251, 191, 36, 0.25);
  }
}

// Min Amount Remark
.min-amount-remark {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  font-size: 0.75rem;
  color: #fca5a5;

  .remark-icon {
    font-size: 0.875rem;
  }
}

// Formula under the amount
.formula-hint {
  display: block;
  margin-top: 8px;
  font-size: 0.75rem;
  color: $text-muted;
}

// Expiry countdown
.countdown-chip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid $border-color;
  border-radius: 8px;
  font-size: 0.75rem;
  color: $text-secondary;

  &.urgent {
    background: $warning-bg;
    border-color: $warning-border;
    color: #fbbf24;
  }
}

// Credit ceiling warning
.credit-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background: $warning-bg;
  border: 1px solid $warning-border;
  border-radius: 8px;
  font-size: 0.75rem;
  color: #fbbf24;

  .remark-icon {
    font-size: 0.875rem;
  }
}

// Inline text-link button (ดูรายการ / เช็คเครดิตอีกครั้ง)
.link-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: inherit;
  color: $gold;
  text-decoration: underline;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

// Claim Button
.claim-btn {
  width: 100%;
  max-width: 220px;
  padding: 12px 24px;
  background: var(--theme-gradient-primary);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 20px rgba(var(--theme-primary-rgb), 0.3);
    }
  }

  &.disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &.claiming {
    opacity: 0.7;
    cursor: wait;
  }

  .btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(#1a1a1a, 0.2);
    border-radius: 50%;
    border-top-color: #1a1a1a;
    animation: spin 0.8s linear infinite;
  }
}

// ==========================================
// Statistics Card
// ==========================================
.stats-card {
  background: $card-bg;
  border-radius: 16px;
  border: 1px solid $border-color;
  backdrop-filter: blur(10px);
  overflow: hidden;
  margin-top: 12px;
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: rgba(59, 130, 246, 0.1);
  border-bottom: 1px solid $border-color;

  .stats-icon {
    font-size: 1rem;
  }

  .stats-title {
    font-size: 0.9375rem;
    font-weight: 600;
    color: #60a5fa;
  }
}

.stats-body {
  padding: 12px 16px;
}

.stat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }

  .stat-label {
    font-size: 0.875rem;
    color: $text-secondary;
  }

  .stat-value {
    font-size: 0.9375rem;
    font-weight: 600;
    color: $text-primary;

    &.positive {
      color: #4ade80;
    }

    &.negative {
      color: #f87171;
    }

    &.highlight {
      color: #ffffff;
    }
  }

  // Net-loss row: the number the percentage actually applies to
  &.net-row {
    .stat-label,
    .stat-value {
      font-weight: 600;
    }
  }

  // Result row
  &.total-row {
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    margin-top: 2px;
    padding-top: 12px;

    .stat-label {
      color: $text-primary;
      font-weight: 600;
    }

    .stat-value.highlight {
      font-size: 1.125rem;
      color: $gold;
    }
  }
}

// Bonus deposit detail rows
.bonus-list {
  margin: 2px 0 6px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;

  .bonus-list-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 0;
    font-size: 0.75rem;
    color: $text-muted;
  }
}

// Why-bonus-deducted explainer
.calc-note {
  padding: 8px 16px;
  font-size: 0.6875rem;
  color: $text-muted;
  line-height: 1.5;
}

.stats-remark {
  padding: 10px 16px;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid $border-color;
  font-size: 0.6875rem;
  color: $text-muted;
  text-align: center;
}

// ==========================================
// Bottom Sheet Modal
// ==========================================
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 9999;
  padding: 0;

  @media (min-width: 768px) {
    align-items: center;
    padding: 20px;
  }
}

.bottom-sheet {
  background: linear-gradient(180deg, #1e1e2e 0%, #16162a 100%);
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid $border-color;
  border-bottom: none;

  @media (min-width: 768px) {
    border-radius: 16px;
    max-width: 400px;
    border-bottom: 1px solid $border-color;
  }
}

.sheet-handle {
  width: 40px;
  height: 4px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  margin: 12px auto;

  @media (min-width: 768px) {
    display: none;
  }
}

.sheet-content {
  padding: 8px 20px 24px;

  @media (min-width: 768px) {
    padding: 24px;
  }
}

.sheet-title {
  text-align: center;
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 20px;
}

// Confirm Amount
.confirm-amount {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: rgba($gold, 0.1);
  border: 1px solid rgba($gold, 0.2);
  border-radius: 10px;
  margin-bottom: 12px;

  .confirm-label {
    font-size: 0.875rem;
    color: $text-secondary;
  }

  .confirm-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #ffffff;
  }
}

// Confirm Notice
.confirm-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  background: $warning-bg;
  border: 1px solid $warning-border;
  border-radius: 8px;
  margin-bottom: 20px;

  .notice-icon {
    font-size: 0.875rem;
  }

  .notice-text {
    font-size: 0.8125rem;
    color: $text-secondary;
  }
}

// Sheet Actions
.sheet-actions {
  display: flex;
  gap: 12px;

  .btn-cancel,
  .btn-confirm {
    flex: 1;
    padding: 14px;
    border-radius: 10px;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    -webkit-tap-highlight-color: transparent;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .btn-cancel {
    background: rgba(255, 255, 255, 0.08);
    color: $text-secondary;
    border: 1px solid $border-color;

    &:active:not(:disabled) {
      background: rgba(255, 255, 255, 0.12);
    }
  }

  .btn-confirm {
    background: linear-gradient(135deg, $gold, $gold-light);
    color: #1a1a1a;
    border: none;

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    .btn-spinner {
      width: 16px;
      height: 16px;
      border: 2px solid rgba(#1a1a1a, 0.2);
      border-radius: 50%;
      border-top-color: #1a1a1a;
      animation: spin 0.8s linear infinite;
    }
  }
}

// ==========================================
// Modal Transition
// ==========================================
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;

  .bottom-sheet {
    transition: transform 0.25s ease;
  }
}

.modal-enter,
.modal-leave-to {
  opacity: 0;

  .bottom-sheet {
    transform: translateY(100%);

    @media (min-width: 768px) {
      transform: translateY(20px) scale(0.95);
    }
  }
}

// ==========================================
// Animations
// ==========================================
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

// ==========================================
// Tablet & Desktop Adjustments
// ==========================================
@media (min-width: 768px) {
  .cashback-page {
    padding: 20px;
  }

  .card-body {
    padding: 24px 20px;
  }

  .amount-box .amount-value {
    font-size: 2.25rem;
  }

  .claim-btn {
    max-width: 240px;
  }
}
</style>
